import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Clock, 
  Menu, 
  X, 
  User, 
  LogOut, 
  LayoutDashboard,
  Users,
  HandHeart,
  MessageSquare,
  History,
  Settings
} from 'lucide-react';

export function Header() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/community', label: 'Community Board', icon: Users },
    { to: '/exchanges', label: 'Exchanges', icon: History },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-[#fef9e7] border-b-3 border-black" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-md bg-[#ffd703] border-2 border-black flex items-center justify-center">
              <Clock className="w-6 h-6 text-black" />
            </div>
            <span className="font-bold text-base text-black font-sans hidden md:block" data-testid="app-name">NCWI</span>
          </Link>

          {/* Desktop Navigation */}
          {user && (
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-sans font-semibold transition-colors border-2 ${
                    isActive(link.to)
                      ? 'bg-[#ffd703] border-black'
                      : 'bg-transparent border-transparent hover:bg-[#ffd703]/30'
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-sans font-semibold transition-colors border-2 ${
                    isActive('/admin')
                      ? 'bg-[#e056a0] text-white border-black'
                      : 'bg-transparent border-transparent hover:bg-[#e056a0]/30'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Admin
                </Link>
              )}
            </nav>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <>
                {/* Credits Badge */}
                <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md bg-[#ffd703] border-2 border-black font-sans font-semibold text-xs sm:text-sm">
                  <Clock className="w-4 h-4" />
                  <span data-testid="credits-display">
                    <span className="sm:hidden">{user.time_credits?.toFixed(1) || '0.0'}</span>
                    <span className="hidden sm:inline">{user.time_credits?.toFixed(1) || '0.0'} credits</span>
                  </span>
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-10 h-10 rounded-md bg-[#d8b4fe] border-2 border-black flex items-center justify-center font-sans font-bold" data-testid="user-menu-trigger">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-lg bg-[#fef9e7] border-2 border-black">
                    <div className="px-3 py-2">
                      <p className="text-sm font-bold font-sans" data-testid="header-name">
                        {user.account_type === 'business' && user.business_name ? user.business_name : user.name}
                      </p>
                      <p className="text-xs text-black/60 font-sans">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-black" />
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer font-sans" data-testid="menu-profile">
                        <User className="w-4 h-4" />
                        My Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-offers" className="flex items-center gap-2 cursor-pointer font-sans" data-testid="menu-my-offers">
                        <HandHeart className="w-4 h-4" />
                        My Offers
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-requests" className="flex items-center gap-2 cursor-pointer font-sans" data-testid="menu-my-requests">
                        <MessageSquare className="w-4 h-4" />
                        My Requests
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-black" />
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="text-red-600 cursor-pointer font-sans"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile Menu Button */}
                <button
                  className="md:hidden w-10 h-10 rounded-md bg-[#fef9e7] border-2 border-black flex items-center justify-center"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <button className="retro-btn retro-btn-outline px-4 py-2 text-sm" data-testid="header-login-btn">
                    Sign In
                  </button>
                </Link>
                <Link to="/register">
                  <button className="retro-btn px-4 py-2 text-sm" data-testid="header-register-btn">
                    Get Started
                  </button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {user && mobileMenuOpen && (
          <div className="md:hidden py-4 border-t-2 border-black animate-fade-in">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 font-sans font-semibold border-2 ${
                    isActive(link.to)
                      ? 'bg-[#ffd703] border-black'
                      : 'bg-transparent border-transparent'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 font-sans font-semibold border-2 ${
                    isActive('/admin')
                      ? 'bg-[#e056a0] text-white border-black'
                      : 'bg-transparent border-transparent'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  Admin
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
